import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import establishmentusers from './submodules/establishmentusers';
import stocks from './submodules/stocks';
import catalogentries from './submodules/catalogentries';
import providerorders from './submodules/providerorders';
import establishmentPossyncs from './submodules/establishment-possyncs';
import establishmentPossales from './submodules/establishment-possales';

const state = {
    section: 'menu',
    establishment: null,
    establishment_loading: false,
    establishment_loading_errors: null,
    establishment_saving: false,
    establishment_saving_errors: null,
    establishment_stats: null,
    establishment_stats_loading: false,
    products_to_order: [],
    products_to_order_loading: false,
};

const mutations = {

    updateEstablishment(state, establishment) {
        state.establishment = establishment;
    },
    updateEstablishmentLoading(state, loading) {
        state.establishment_loading = loading;
    },
    updateEstablishmentLoadingErrors(state, errors) {
        state.establishment_loading_errors = errors;
    },
    updateEstablishmentSaving(state, saving) {
        state.establishment_saving = saving;
    },
    updateEstablishmentSavingErrors(state, errors) {
        state.establishment_saving_errors = errors;
    },
    updateEstablishmentStats(state, stats) {
        state.establishment_stats = stats;
    },
    updateEstablishmentStatsLoading(state, loading) {
        state.establishment_stats_loading = loading;
    },
    updateSection(state, section) {
        state.section = section;
    },
    updateProductsToOrder(state, products) {
        state.products_to_order = products;
    },
    updateProductsToOrderLoading(state, loading) {
        state.products_to_order_loading = loading;
    },
};

const actions = {

    async fetchEstablishment({ commit, dispatch, state }, params) {
        commit('updateEstablishmentLoading', true);
        commit('updateEstablishmentLoadingErrors', null);
        let url;
        if (params && params.establishment) {
            url = `/api/establishments/${params.establishment}/`;
        } else if (state.establishment) {
            url = `/api/establishments/${state.establishment.id}/`;
        } else {
            throw 'No establishment to fetch';
        }
        try {
            const response = await axios.get(url);
            commit('updateEstablishment', response.data);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateEstablishmentLoadingErrors', error);
            throw error;
        } finally {
            commit('updateEstablishmentLoading', false);
        }
    },

    async saveEstablishment({ commit, dispatch, state }, params) {
        commit('updateEstablishmentSaving', true);
        commit('updateEstablishmentSavingErrors', {});

        let url = '/api/establishments/';
        let method = axios.post;

        if (params.instance.id) {
            url = `/api/establishments/${params.instance.id}/`;
            method = axios.put;
        }
        try {
            const response = await method(url, params.instance);
            commit('updateEstablishmentSaving', false);
            commit('updateEstablishmentSavingErrors', {});
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateEstablishmentSavingErrors', error.details);
            throw error;
        }
    },

    async fetchEstablishmentStats({ commit, dispatch, state }, params) {
        commit('updateEstablishmentStatsLoading', true);

        let establishment_id;
        if (params && params.establishment) {
            establishment_id = params.establishment;
        } else if (state.establishment) {
            establishment_id = state.establishment.id;
        } else {
            throw 'No establishment to fetch stats for';
        }

        try {
            const response = await axios.get(`/api/establishments/${establishment_id}/stats/`);
            commit('updateEstablishmentStatsLoading', false);
            commit('updateEstablishmentStats', response.data);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateEstablishmentStatsLoading', false);
            throw error;
        }
    },

    async fetchProductsToOrder({ commit, state }, params) {
        commit('updateProductsToOrderLoading', true);

        let establishment_id;
        if (params && params.establishment_id) {
            establishment_id = params.establishment_id;
        } else if (state.establishment) {
            establishment_id = state.establishment.id;
        } else {
            throw 'No establishment to fetch products to order for';
        }

        try {
            const response = await axios.get('/api/shop/to-order/', {
                params: { establishment: establishment_id },
            });
            commit('updateProductsToOrder', response.data);
            commit('updateProductsToOrderLoading', false);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateProductsToOrder', []);
            commit('updateProductsToOrderLoading', false);
            throw error;
        }
    },

    async refreshWarehouses({ dispatch, state }, params = {}) {
        const establishment_id =
            params.establishment_id ??
            params.establishment ??
            state.establishment?.id;
        if (!establishment_id) {
            return;
        }
        await dispatch('stocks/init', {
            filters: { establishment: establishment_id },
        });
        await dispatch('stocks/fetchStocks');
        await dispatch('fetchEstablishmentStats', { establishment: establishment_id });
    },

    async init({ commit, dispatch, state }, params) {
        commit("updateEstablishmentStats", null);

        await dispatch("establishmentusers/init", {
            filters: { establishment: params.establishment.id },
        });
        await dispatch("stocks/init", {
            filters: { establishment: params.establishment.id },
        });
        await dispatch("menuentries/init", {
            filters: { establishment: params.establishment.id },
            fetch_stats: true,
            storageOptions: {
                storageKey: `establishment-menu-${params.establishment.id}`,
                fieldsToSave: ['ordering', 'limit'],
            },
        });
        await dispatch("possyncs/init", {
            filters: { establishment: params.establishment.id },
        });
        await dispatch("possales/init", {
            filters: { establishment: params.establishment.id },
        });
        await commit("updateSection", params.section || "menu");
        await commit("updateEstablishment", params.establishment);
        dispatch("stocks/fetchStocks");
        dispatch("establishmentusers/fetchEstablishmentUsers");
        dispatch("fetchEstablishmentStats");
        dispatch("fetchProductsToOrder");
    },

};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        establishmentusers: establishmentusers(),
        stocks: stocks(),
        menuentries: catalogentries(),
        providerorders: providerorders(),
        possyncs: establishmentPossyncs(),
        possales: establishmentPossales(),
    },
};

